import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { hasModuleFromToken, isAdminFromToken, isDpOrAdminFromToken } from "../../../utils/authToken";
import { HOMEPAGE } from "../../home/routes";
import { DP_SETTINGS_PAGE } from "../../dp/routes";
import KioskDevicesSettings from "../../dp/components/KioskDevicesSettings";
import StoreScheduleForm from "../../dp/components/StoreScheduleForm";
import { STOREPAGE } from "../routes";
import { deactivateStoreByIdApi, getStoreByIdApi, reactivateStoreByIdApi, StorePublic } from "../storeApi";
import StoreDataForm from "../components/StoreDataForm";
import StoreLocationForm from "../components/StoreLocationForm";
import StoreTeamTab from "../components/StoreTeamTab";

import "../../dp/pages/Dp.css";
import "./StorePage.css";
import "./StoreDetailPage.css";

type TabKey = "dados" | "horario" | "posicao" | "equipe" | "quiosque";

const TAB_LABELS: Record<TabKey, string> = {
  dados: "Dados",
  horario: "Horário e turnos",
  posicao: "Posição",
  equipe: "Equipe",
  quiosque: "Quiosque",
};

/**
 * Ficha da loja (plan-notificacoes-whatsapp, 22/10/2026): um lugar só para o que
 * antes ficava dividido entre Operações → Lojas e DP → Cadastros → Lojas e turnos.
 * Abre pelos dois caminhos. Cada aba segue o que o backend deixa cada papel
 * fazer: o ADMIN edita os dados da loja; ADMIN e DP editam o horário, a posição,
 * a lotação e o quiosque; o DP vê os dados só para leitura. O administrador
 * restrito perde as abas do módulo que lhe foi retirado (ADR 0027).
 */
export function StoreDetailPage() {
  const navigate = useNavigate();
  const { storeId = "" } = useParams<{ storeId: string }>();
  const [params, setParams] = useSearchParams();
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const canOperations = isAdminFromToken(token) && hasModuleFromToken(token, "OPERACOES");
  const canDp = isDpOrAdminFromToken(token) && hasModuleFromToken(token, "DP");
  const canAdminUsers = isAdminFromToken(token) && hasModuleFromToken(token, "ADMINISTRACAO");
  // Quem chega pelo DP volta para Cadastros; pela lista de lojas, para ela.
  const goBack = useGoBack(canOperations ? STOREPAGE() : `${DP_SETTINGS_PAGE()}?aba=lojas`);

  const [store, setStore] = useState<StorePublic | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [dataSaving, setDataSaving] = useState(false);
  const [dataValid, setDataValid] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [toggling, setToggling] = useState(false);

  const isDeactivated = !!store?.deletedAt;

  const tabs = useMemo<TabKey[]>(() => {
    const list: TabKey[] = ["dados"];
    if (canDp && !isDeactivated) list.push("horario", "posicao", "equipe", "quiosque");
    else if (canAdminUsers && !isDeactivated) list.push("equipe");
    return list;
  }, [canDp, canAdminUsers, isDeactivated]);

  const requested = params.get("aba") as TabKey | null;
  const tab: TabKey = requested && tabs.includes(requested) ? requested : "dados";
  const setTab = (next: TabKey) => {
    const nextParams = new URLSearchParams(params);
    nextParams.set("aba", next);
    setParams(nextParams, { replace: true });
  };

  const load = useCallback(async () => {
    if (!token || !storeId) return;
    try {
      setStore(await getStoreByIdApi(token, storeId));
    } catch {
      setNotFound(true);
    }
  }, [token, storeId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleValid = useCallback((v: boolean) => setDataValid(v), []);

  const confirmToggle = async () => {
    if (!store) return;
    setToggling(true);
    try {
      if (isDeactivated) {
        await reactivateStoreByIdApi(store.id);
        toast.success("Loja reativada com sucesso!");
      } else {
        await deactivateStoreByIdApi(store.id);
        toast.success("Loja desativada com sucesso!");
      }
      setToggleOpen(false);
      await load();
    } catch (error) {
      toast.error(error instanceof Error && error.message ? error.message : "Erro ao alterar a situação da loja.");
    } finally {
      setToggling(false);
    }
  };

  const crumbs = [
    { label: "Início", onClick: () => navigate(HOMEPAGE()) },
    canOperations
      ? { label: "Lojas", onClick: () => navigate(STOREPAGE()) }
      : { label: "Cadastros do DP", onClick: () => navigate(`${DP_SETTINGS_PAGE()}?aba=lojas`) },
    { label: store?.nomeFantasia ?? "Loja" },
  ];

  if (notFound) {
    return (
      <div className="storeDetail">
        <main className="storeDetail__main">
          <EcoPageHeader crumbs={crumbs} title="Loja não encontrada" onBack={goBack} backLabel="Voltar" />
          <div className="ecoCard ecoEmpty">Esta loja não existe ou foi removida.</div>
        </main>
      </div>
    );
  }

  return (
    <div className="storeDetail">
      <main className="storeDetail__main">
        <EcoPageHeader
          crumbs={crumbs}
          title={store?.nomeFantasia ?? "Carregando..."}
          subtitle={
            store
              ? [store.estado, store.endereco].filter(Boolean).join(" · ") || "Sem endereço cadastrado"
              : undefined
          }
          onBack={goBack}
          backLabel="Voltar"
          actions={
            store ? (
              <span className={`ecoPill${isDeactivated ? " ecoPill--off" : ""}`}>{isDeactivated ? "Desativada" : "Ativa"}</span>
            ) : null
          }
        />

        <nav className="storeDetail__tabs" aria-label="Seções da loja">
          {tabs.map((key) => (
            <button
              key={key}
              type="button"
              className={`storeDetail__tab${tab === key ? " is-current" : ""}`}
              aria-current={tab === key ? "page" : undefined}
              onClick={() => setTab(key)}
            >
              {TAB_LABELS[key]}
            </button>
          ))}
        </nav>

        {store && token ? (
          <section className="ecoCard storeDetail__panel">
            {tab === "dados" ? (
              <>
                {!canOperations ? (
                  <p className="dpNotice">Os dados da loja são editados pelo administrador, em Operações → Lojas.</p>
                ) : isDeactivated ? (
                  <p className="dpNotice dpNotice--warning">Loja desativada. Reative para poder editar.</p>
                ) : null}
                <StoreDataForm
                  key={`${store.id}-${store.nomeFantasia}`}
                  store={store}
                  formId="storeDetail-data"
                  readOnly={!canOperations || isDeactivated}
                  onSavingChange={setDataSaving}
                  onValidChange={handleValid}
                  onSaved={(saved) => setStore((prev) => (prev ? { ...prev, ...saved } : saved))}
                />
                {canOperations ? (
                  <div className="storeTab__actions storeTab__actions--split">
                    <button
                      type="button"
                      className={`ecoBtn ecoBtn--sm${isDeactivated ? "" : " ecoBtn--danger"}`}
                      onClick={() => setToggleOpen(true)}
                      disabled={dataSaving}
                    >
                      {isDeactivated ? "Reativar loja" : "Desativar loja"}
                    </button>
                    {!isDeactivated ? (
                      <button type="submit" form="storeDetail-data" className="ecoBtn ecoBtn--primary" disabled={!dataValid || dataSaving}>
                        {dataSaving ? "Salvando..." : "Salvar dados"}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : null}

            {tab === "horario" ? <StoreScheduleForm token={token} storeId={store.id} /> : null}
            {tab === "posicao" ? <StoreLocationForm token={token} storeId={store.id} /> : null}
            {tab === "equipe" ? (
              <StoreTeamTab token={token} storeId={store.id} storeName={store.nomeFantasia} showStaff={canDp} showSupervisors={canAdminUsers} />
            ) : null}
            {tab === "quiosque" ? <KioskDevicesSettings token={token} storeId={store.id} /> : null}
          </section>
        ) : (
          <div className="ecoCard ecoEmpty">Carregando...</div>
        )}
      </main>

      <EcoConfirmDialog
        open={toggleOpen}
        title={isDeactivated ? "Reativar loja?" : "Desativar loja?"}
        message={
          store ? (
            isDeactivated ? (
              <>
                <strong>{store.nomeFantasia}</strong> volta a aparecer nas escalas e avaliações.
              </>
            ) : (
              <>
                <strong>{store.nomeFantasia}</strong> deixa de aparecer nas escalas e avaliações. Dá para reativar depois.
              </>
            )
          ) : null
        }
        confirmLabel={isDeactivated ? "Reativar" : "Desativar"}
        tone={isDeactivated ? "default" : "danger"}
        busy={toggling}
        onConfirm={confirmToggle}
        onCancel={() => setToggleOpen(false)}
      />
    </div>
  );
}
